import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAnalysisStocks } from "../../hooks/useAnalysisStocks";
import { StateContext } from "../../store/context";
import { TitleArea } from "../../components/Typography/titleArea";
import { LoadingData, NoDataDetails } from "./components/noDetails";
import { StockSelectOptions } from "./components/stockSelectOptions";
import { usePreventCopy } from "../../hooks/usePreventCopy";
import { LineChartOutlined } from "@ant-design/icons";
import ROUTES from "../../setup/routes";
import CompanyHeader from "./components/CompanyHeader";
import BusinessModel from "./components/BusinessModel";
import HowYouEarn from "./components/HowYouEarn";
import PriceHistory from "./components/PriceHistory";
import PeerComparison from "./components/PeerComparison";
import RiskWarning from "./components/RiskWarning";
import ModelReasoning from "./components/ModelReasoning";
import BeginnerDisclaimer from "./components/BeginnerDisclaimer";
import QuickSnapshot from "./components/QuickSnapshot";
import Glossary from "./components/Glossary";
import { CompanyCardData } from "./components/types";
import DisclaimerButton from "../../components/DisclaimerButton";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./beginner.theme.css";

const BeginnerScreen: React.FC = (): React.ReactElement => {
  usePreventCopy();
  const navigate = useNavigate();

  const {
    baseCurrency,
    authUser: { allowed, sub_plan, role_name },
  } = useContext(StateContext);

  const [selectedStock, setSelectedStock] = useState<any>(null);
  const [cardData, setCardData] = useState<CompanyCardData | null>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [sectorPeers, setSectorPeers] = useState<any[]>([]);

  const {
    response,
    loading: stockLoading,
  } = useAnalysisStocks(selectedStock?.id, selectedStock?.beginner_summary);

  useEffect(() => {
    if (!selectedStock?.id || !response) return;
    const refreshedStock = response
      .flatMap((section: any) => section.options ?? [])
      .find((stock: any) => Number(stock.id) === Number(selectedStock.id));

    if (refreshedStock?.beginner_summary) {
      setSelectedStock(refreshedStock);
    }
  }, [response, selectedStock?.id]);

  useEffect(() => {
    if (!selectedStock || !response) return;
    const allStocks = response.flatMap((section: any) => section.options ?? []);
    const peers = allStocks.filter(
      (s: any) =>
        s.sector_id === selectedStock.sector_id &&
        s.stock_market_id === selectedStock.stock_market_id,
    );
    setSectorPeers(peers);
  }, [selectedStock, response]);

  useEffect(() => {
    if (!selectedStock) {
      setCardData(null);
      setParseError(null);
      return;
    }

    if (!selectedStock.beginner_summary) {
      console.warn("No beginner_summary found on selected stock");
      setCardData(null);
      setParseError("No beginner summary available for this stock.");
      return;
    }

    try {
      const cleaned = selectedStock.beginner_summary
        .trim()
        .replace(/^'|'$/g, "")
        .replace(/''/g, "'");

      const data: CompanyCardData = JSON.parse(cleaned);

      setParseError(null);
      setCardLoading(true);

      const timer = setTimeout(() => {
        setCardData(data);
        setCardLoading(false);
      }, 800);

      return () => clearTimeout(timer);
    } catch (err) {
      console.error("Failed to parse beginner_summary:", err);
      setCardData(null);
      setCardLoading(false);
      setParseError("Failed to load company card. Please try another stock.");
    }
  }, [selectedStock]);

  // Hands the currently selected stock off to Single Stock Analysis via
  // the same localStorage key that page's own StockSelectOptions already
  // restores its selection from on mount (see its STORAGE_KEY/restore
  // effect) — no route param or shared state needed, just pre-seeding
  // the value the destination page already knows how to read.
  const handleViewFullAnalysis = () => {
    if (!selectedStock?.id) return;
    localStorage.setItem(
      "single_stock_analysis_selected_ticker",
      JSON.stringify({ id: selectedStock.id }),
    );
    navigate(`${ROUTES.DASHBOARD}/${ROUTES.SINGLE_STOCK_ANALYSIS}`);
  };

  return (
    <div className="vi-beg vi-glass-sheen flex md:flex-row flex-col items-center md:items-start w-full select-none">
      <div className="flex flex-col w-full h-full items-start px-4 py-3 pb-10">
        {/* Page title */}
        <div className="w-full flex items-start justify-between gap-4">
          <TitleArea
            variant="page"
            title="Beginner Analysis"
            subTitle="Everything a first time investor needs to know about a company, in one place."
          />
          <DisclaimerButton />
        </div>
        <hr className="hr-line w-full my-2" />

        {/* Stock picker */}
        <div className="w-full flex items-center justify-between gap-4 flex-wrap">
          <div className="w-full md:w-[500px]">
            <StockSelectOptions
              response={response}
              loading={stockLoading}
              selectedStock={selectedStock}
              setSelectedStock={setSelectedStock}
            />
          </div>

          {selectedStock && (
            <button
              type="button"
              onClick={handleViewFullAnalysis}
              className="vi-beg-full-analysis-btn flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold whitespace-nowrap"
              style={{
                background: "var(--vi-accent-grad)",
                color: "var(--vi-accent-on)",
              }}
            >
              Analyze in Detail
              <ArrowRight size={13} />
            </button>
          )}
        </div>

        <hr className="hr-line w-full my-2" />

        {/* Body */}
        {cardLoading ? (
          <LoadingData />
        ) : !selectedStock ? (
          <NoDataDetails />
        ) : parseError ? (
          <div className="w-full flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <LineChartOutlined className="text-primary text-2xl" />
            </div>
            <p className="text-neutral font-dm font-semibold text-[16px] m-0">
              Analysis not available yet
            </p>
            <p className="text-neutral/60 font-dm text-sm m-0 text-center max-w-[340px]">
              The beginner summary for this stock hasn't been generated yet.
              Please try another stock or check back later.
            </p>
          </div>
        ) : cardData ? (
          <div className="w-full grid grid-cols-[1fr_332px] gap-6 mt-4">
            {/* Main column */}
            <main className="min-w-0">
              <CompanyHeader data={cardData} selectedStock={selectedStock} />
              <BusinessModel data={cardData} />
              <HowYouEarn data={cardData} />
              <PriceHistory
                selectedStock={selectedStock}
                baseCurrency={baseCurrency}
                bodyText={cardData.price_history_text}
              />
              <PeerComparison data={cardData} />
              <ModelReasoning steps={cardData.reasoning_steps} />
            </main>

            {/* Sidebar */}
            <aside className="flex flex-col gap-4 sticky top-20 self-start xl:static">
              <QuickSnapshot rows={cardData.snapshot_rows} />
              <Glossary entries={cardData.glossary} />
              <RiskWarning risks={cardData.risks} />
              <BeginnerDisclaimer />
            </aside>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BeginnerScreen;
